import * as React from "react";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";
import NumberFormat from "react-number-format";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { ThemeProvider, Tooltip, Typography } from "@material-ui/core";

// Utility
import Utility from "../../domain/classes/Utility";

// Font
import "@fontsource/share-tech-mono";

// Utility
import medicareTheme from "../../domain/utility/medicareTheme";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";

// Classes
import Medicare from "../../domain/classes/Medicare";
import AuditableDocument from "../../domain/classes/AuditableDocument";

// Errors
import InvalidExpiryError from "../../domain/classes/errors/InvalidExpiryError";

// Interfaces
import IMedicareCardError, {
	InitialMedicareCardErrorState,
} from "../../domain/interfaces/IMedicareCardError";
import Patient from "../../domain/classes/Patient";
import { BLANK_MEDICARE } from "../../domain/utility/config";

export const styles = (theme: Theme) =>
	createStyles({
		imgContainer: { width: "100%", overflow: "hidden" },
		img: { width: "100%" },
		root: { position: "relative", display: "block" },
		medicareCardNumberContainer: {
			position: "absolute",
			width: "80%",
			top: 60,
			left: "50%",
			transform: "translateX(-50%)",
		},
		medicareCardNumber: {
			fontFamily: "Share Tech Mono",
			fontSize: theme.spacing(3),
			border: "none",
			background: "transparent",
			fontWeight: "bold",
			outline: "none",
		},
		irnDetailsContainer: {
			position: "absolute",
			display: "inline",
			top: 100,
			left: theme.spacing(2),
		},
		irnDetails: {
			fontFamily: "Share Tech Mono",
			padding: theme.spacing(1, 0),
			fontSize: theme.spacing(2),
			border: "none",
			background: "transparent",
			width: theme.spacing(5),
			fontWeight: "bold",
			outline: "none",
		},
		fullNameTextField: {
			fontFamily: "Share Tech Mono",
			padding: theme.spacing(1, 0),
			fontSize: theme.spacing(2),
			border: "none",
			background: "transparent",
			marginLeft: theme.spacing(1),
			fontWeight: "bold",
			textTransform: "uppercase",
			outline: "none",
		},
		expiryDetailsContainer: {
			display: "flex",
			flexDirection: "row-reverse",
		},
		expiryWrapper: {
			position: "absolute",
			top: 150,
			fontFamily: "Share Tech Mono",
			fontSize: theme.spacing(3),
			right: theme.spacing(1),
		},
		expiryDetails: {
			fontFamily: "Share Tech Mono",

			fontSize: theme.spacing(2),
			border: "none",
			background: "transparent",
			marginLeft: theme.spacing(2),
			width: "80px",
			fontWeight: "bold",
			outline: "none",
		},
		validToText: {
			display: "flex",
			alignItems: "center",
			fontSize: theme.spacing(2),
			fontWeight: "bold",
		},
	});

export interface MedicareCardProps
	extends React.HTMLAttributes<HTMLDivElement> {}

const MedicareCard = React.forwardRef<
	HTMLDivElement,
	MedicareCardProps & WithStyles<typeof styles>
>(({ classes, className, ...divProps }, ref) => {
	// Contexts
	const {
		handleMedicareCardErrorChange,
		handleMedicareCardChange,
		medicareCardError,
		medicareCard,
		patientDetails,
	} = useApplicationManager();

	React.useEffect(() => {
		// console.log(medicareCard);

		try {
			const expiry = AuditableDocument.formatExpiry(medicareCard.expiry);
			if (Medicare.expiryIsInvalid(expiry)) {
				throw new InvalidExpiryError();
			} else {
				handleMedicareCardErrorChange({
					...InitialMedicareCardErrorState,
				});
			}
		} catch (error) {
			handleMedicareCardErrorChange({
				...medicareCardError,
				expiryError: error.message,
			});
		}

		const { error } = Medicare.schema.validate(medicareCard);

		if (error) {
			const { details } = error;

			let errorObj: IMedicareCardError = {
				...InitialMedicareCardErrorState,
			};

			for (let i = 0; i < details.length; i++) {
				const currentDetail = details[i];
				const key = currentDetail.path[0] + "Error";
				errorObj = { ...errorObj, [key]: currentDetail.message };
			}

			handleMedicareCardErrorChange(errorObj);
		}
	}, [medicareCard]);

	// Handler
	const handleMedicareCardNumberBlur = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		const val = event.target.value;
		// Utility.debug("Medicare number", val);
		const sanitisedVal = Medicare.sanitise(val);

		handleMedicareCardChange({
			...medicareCard,
			medicareNumber: sanitisedVal,
		});
	};

	const handleIrnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		handleMedicareCardChange({
			...medicareCard,
			irn: parseInt(event.target.value),
		});
	};

	const handleMedicareExpiryBlur = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handleMedicareCardChange({
			...medicareCard,
			expiry: event.target.value,
		});
	};

	return (
		<div className={clsx(classes.root, className)} ref={ref} {...divProps}>
			<div className={classes.imgContainer}>
				<img
					className={classes.img}
					src={BLANK_MEDICARE}
					alt="Blank medicare card patient interaction"
				/>
			</div>
			<div className={classes.medicareCardNumberContainer}>
				<Tooltip
					title="Enter your Medicare card number"
					arrow
					placement="top"
				>
					<NumberFormat
						format="#### ##### #"
						mask="_"
						className={classes.medicareCardNumber}
						placeholder="Medicare number"
						onBlur={handleMedicareCardNumberBlur}
						tabIndex={1}
						autoFocus
					/>
				</Tooltip>
			</div>
			<div className={classes.irnDetailsContainer}>
				<Tooltip
					title="IRN is the number in front of your name."
					arrow
					placement="top"
				>
					<NumberFormat
						format="#"
						mask="_"
						placeholder="IRN"
						className={classes.irnDetails}
						tabIndex={2}
						onBlur={handleIrnBlur}
					/>
				</Tooltip>
				<Tooltip title="Your name as it appears on your Medicare card.">
					<ThemeProvider theme={medicareTheme}>
						{/* <input
							placeholder="Full name"
							className={classes.fullNameTextField}
							tabIndex={3}
							onBlur={handleMedicareNameBlur}
						/> */}
						<Typography
							className={classes.fullNameTextField}
							display="inline"
						>
							{Patient.generateFullName(patientDetails, true)}
						</Typography>
					</ThemeProvider>
				</Tooltip>
			</div>
			<div className={classes.expiryWrapper}>
				<div className={classes.expiryDetailsContainer}>
					<Tooltip title="Expiry date of your Medicare card.">
						<ThemeProvider theme={medicareTheme}>
							<NumberFormat
								format="##/####"
								mask="_"
								placeholder="Expiry"
								className={classes.expiryDetails}
								tabIndex={4}
								onBlur={handleMedicareExpiryBlur}
							/>
						</ThemeProvider>
					</Tooltip>
					<div className={classes.validToText}>
						<span>VALID TO</span>
					</div>
				</div>
			</div>
		</div>
	);
});

const StyledMedicareCard = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("MedicareCard"),
})(MedicareCard);

export default StyledMedicareCard;

export const AnimatedMedicareCard = animated(StyledMedicareCard);
